import { GoalContextView } from "@/components/flow/goal-context-view";
import type { CompletionMarkers } from "@/lib/hooks/use-flow-instances/use-flow-completion";
import {
  useCreatePortfolio,
  useDeletePortfolio,
  useFlowInstancePortfolios,
} from "@/lib/hooks/use-flow-instances/use-portfolios";
import type { FlowPage } from "@/lib/types/flow";

type AdvisorGoalContextProps = {
  goalId: string;
  instanceId: string;
  isCompleted: boolean;
  multiPortfolio: boolean;
  goalPages: FlowPage[];
  portfolioPages: FlowPage[];
  /** QL-120 — per-goal / per-portfolio page-level conditional visibility. */
  isGoalPageVisible?: (page: FlowPage, goalId: string) => boolean;
  isPortfolioPageVisible?: (page: FlowPage, goalId: string, portfolioId: string) => boolean;
  renderGoalPage: (page: FlowPage, goalId: string) => React.ReactNode;
  renderPortfolioPage: (page: FlowPage, goalId: string, portfolio: any) => React.ReactNode;
  // Present only in the single-goal (hosted-globals, flat top row) case.
  globalPagesBefore?: FlowPage[];
  globalPagesAfter?: FlowPage[];
  renderGlobalPage?: (page: FlowPage) => React.ReactNode;
  showValidationErrors?: boolean;
  markers: CompletionMarkers;
};

/**
 * Advisor per-goal data wrapper (QP-150). Fetches one goal's portfolios and wires the
 * add/remove-portfolio actions, then feeds them to the presentational {@link GoalContextView}.
 *
 * Mounted once for the active goal (multi-goal) or for the sole goal (single-goal), so the portfolio
 * hooks run exactly once per rendered goal. Holds no tab/selection state and no layout — just data +
 * actions + pass-through of the render-props.
 */
export function AdvisorGoalContext({
  goalId,
  instanceId,
  isCompleted,
  multiPortfolio,
  goalPages,
  portfolioPages,
  isGoalPageVisible,
  isPortfolioPageVisible,
  renderGoalPage,
  renderPortfolioPage,
  globalPagesBefore,
  globalPagesAfter,
  renderGlobalPage,
  showValidationErrors,
  markers,
}: AdvisorGoalContextProps) {
  const { portfolios, isLoading } = useFlowInstancePortfolios(goalId);
  const createPortfolio = useCreatePortfolio(goalId);
  const deletePortfolio = useDeletePortfolio();

  return (
    <GoalContextView
      goalId={goalId}
      goalPages={goalPages}
      portfolioPages={portfolioPages}
      isGoalPageVisible={isGoalPageVisible}
      isPortfolioPageVisible={isPortfolioPageVisible}
      multiPortfolio={multiPortfolio}
      isCompleted={isCompleted}
      portfolios={portfolios}
      isLoadingPortfolios={isLoading}
      isAddingPortfolio={createPortfolio.isPending}
      onAddPortfolio={(name, onCreated) =>
        createPortfolio.mutate(
          { name },
          {
            onSuccess: (created) => {
              const id = (created as { id?: string } | undefined)?.id;
              if (id) onCreated(id);
            },
          },
        )
      }
      onRemovePortfolio={(portfolioId) =>
        deletePortfolio.mutate({ portfolioId, goalId, instanceId })
      }
      renderGoalPage={renderGoalPage}
      renderPortfolioPage={renderPortfolioPage}
      globalPagesBefore={globalPagesBefore}
      globalPagesAfter={globalPagesAfter}
      renderGlobalPage={renderGlobalPage}
      showValidationErrors={showValidationErrors}
      markers={markers}
    />
  );
}
